import { useState, useEffect, useCallback, useRef } from 'react';
import {
    fetchGatewayConfig,
    updateGatewayConfig,
    checkGatewayHealth,
    GatewaySettings,
} from '../services/gateway/settings';

/**
 * Hook for managing Equabot Gateway configuration (Spec 087).
 * Provides config state, save/refresh actions, and gateway health status.
 */
export function useEquabotConfig() {
    const [config, setConfig] = useState<GatewaySettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [gatewayConnected, setGatewayConnected] = useState(false);
    const [gatewayVersion, setGatewayVersion] = useState<string | undefined>();
    const pollingRef = useRef<number>();

    /** Load config from Gateway */
    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [cfg, health] = await Promise.all([
                fetchGatewayConfig(),
                checkGatewayHealth(),
            ]);
            setConfig(cfg);
            setGatewayConnected(health.connected);
            setGatewayVersion(health.version);
            if (!cfg && !health.connected) {
                setError('Gateway unreachable');
            }
        } catch {
            setError('Failed to load configuration');
        } finally {
            setLoading(false);
        }
    }, []);

    /** Save partial config update */
    const save = useCallback(async (patch: Partial<GatewaySettings>) => {
        setSaving(true);
        setError(null);
        const result = await updateGatewayConfig(patch);
        if (!result.ok) {
            setError(result.error || 'Save failed');
        } else {
            // Refresh to get server-confirmed state
            await refresh();
        }
        setSaving(false);
        return result.ok;
    }, [refresh]);

    // Initial load
    useEffect(() => {
        refresh();
    }, [refresh]);

    // Health polling every 30s
    useEffect(() => {
        pollingRef.current = window.setInterval(async () => {
            const health = await checkGatewayHealth();
            setGatewayConnected(health.connected);
            setGatewayVersion(health.version);
        }, 30000);
        return () => window.clearInterval(pollingRef.current);
    }, []);

    return {
        config,
        loading,
        saving,
        error,
        gatewayConnected,
        gatewayVersion,
        refresh,
        save,
        setError,
    };
}
